/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import React, { ReactElement, useEffect, useState } from 'react';
import {
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridCellFocusMode,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridProps,
    DataGridRow,
    makeStyles,
    mergeClasses,
    shorthands,
    TableCellLayout,
    TableColumnDefinition,
    TableColumnId,
    TableColumnSizingOptions,
    TableHeaderCell,
    TableRowId,
    Text,
    Toolbar,
    ToolbarButton,
    ToolbarDivider,
    Tooltip,
} from '@fluentui/react-components';
import { useCommonStyles } from '../../common/Styles/commonStyles';
import { CustomDataTableContextMenu } from './CustomDataTableContextMenu';
import { MoreVertical16Regular, MoreVertical20Regular, MoreVerticalRegular } from '@fluentui/react-icons';
import { Column, ColumnProps, Row } from '../../common';
import { appTokens } from '../../common/Styles/appTokens';
import { COACHMARK_ATTRIBUTE_NAME } from '@fluentui/react';

const useStyles = makeStyles({
    root: {
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    dataGridContainer: {
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    gridBody: {
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '99%',
    },
    gridBodyBorder: {
        border: '1px solid green',
    },
    headerRow: {
        backgroundColor: appTokens.table.header.background,
        borderBlockEndColor: appTokens.table.header.border,
    },
    headerCell: {
        fontWeight: appTokens.font.weight.bold,
    },
    cell: {
        pointerEvents: 'none',
        userSelect: 'none',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingBlock: '.75rem',
    },
    cellPointerOn: {
        pointerEvents: 'unset',
    },
    cellUserSelectOn: {
        userSelect: 'unset',
    },
    cellBorder: {
        border: '1px solid blue',
    },
    cellJustifyStart: {
        justifyContent: 'flex-start',
    },
    cellJustifyCenter: {
        justifyContent: 'center',
    },
    cellJustifyEnd: {
        justifyContent: 'flex-end',
    },
    fullWidth: {
        width: '100%',
        display: 'flex', // important: flex, not inline-flex
        alignItems: 'center',
    },
    endCell: {
        justifyContent: 'flex-end',
        paddingInlineEnd: '1rem',
        overflow: 'hidden',
    },
    dataGridRow: {
        '& .hidden-btn': {
            visibility: 'hidden',
        },
        '&:hover': {
            '& .hidden-btn': {
                visibility: 'visible',
            },
        },
    },
    actionsToolbar: {
        width: '100%',
        justifyContent: 'flex-end',
        minWidth: 0, // Allow toolbar to shrink
    },
    actionsContainer: {
        width: 'fit-content',
        maxWidth: '100%',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'flex-end',
        minWidth: 0, // Allow container to shrink
    },
    actionDivider: {
        '::before': {
            ...shorthands.borderColor(appTokens.border.colors.subtle),
        },
        '::after': {
            ...shorthands.borderColor(appTokens.border.colors.subtle),
        },
    },
});

export type CustomDataTableColumn<T> = {
    id: string;
    compare?: (a: T, b: T) => number;
    header: string | JSX.Element;
    renderCell: (item: T) => JSX.Element;
    show?: boolean;
    cellFocusMode?: 'group' | 'cell' | 'none';
    defaultWidth?: number;
    minWidth?: number;
    pointerEvents?: boolean;
    userSelect?: boolean;
    justifyContent?: 'start' | 'center' | 'end';
    border?: boolean;
    primary?: boolean;
};

export const CustomTableCellLayout = ({ children, ...otherProps }: { children: React.ReactNode } & ColumnProps) => {
    return (
        <Column padding='py-2' {...otherProps}>
            {children}
        </Column>
    );
};

export type CustomDataTableActionDialogProps<T> = {
    item?: T;
    children?: ReactElement<any>;
    onClickTrigger?: () => void;
};

export type CustomDataTableAction<T> = {
    id: string;
    divider?: boolean;
    group?: string;
    label: string;
    tooltip?: string;
    icon?: JSX.Element | ((item: T) => JSX.Element);
    onClick?: (item: T, e?: React.MouseEvent<any>) => void;
    onMenuClick?: (item: T, e?: React.MouseEvent<any>) => void;
    singleSelectItemOnClick?: boolean;
    renderDialog?: (item: T, child?: JSX.Element, open?: boolean, onOpenChange?: (isOpen: boolean) => void) => JSX.Element;
    canAccess?: (item?: T) => boolean;
    ariaLabel?: string;
    availableForMultiselect?: boolean;
    noHideOnHover?: (item: T) => boolean;
    disabled?: boolean | ((item: T) => boolean);
};

export type CustomDataTableProps<T> = {
    items: T[];
    rowId: string;
    columns?: CustomDataTableColumn<T>[];
    actions?: CustomDataTableAction<T>[];
    onSelectionChanged?: (items: T[]) => void;
    singleSelect?: boolean;
    bodyHeight?: string;
    bodyBorder?: boolean;
    onDoubleClickItem?: (item: T, e?: React.MouseEvent<any>) => void;
    select?: boolean;
    sortable?: boolean;
    clearSelectionToken?: string;
    showActions?: boolean;
};
export const CustomDataTable = <T,>(props: CustomDataTableProps<T>) => {
    const {
        items = [],
        rowId,
        columns,
        actions,
        onSelectionChanged,
        singleSelect = false,
        bodyHeight,
        onDoubleClickItem,
        select = true,
        sortable = true,
        clearSelectionToken,
        bodyBorder = false,
        showActions = true,
    } = props;

    //State
    const [menuOpen, setMenuOpen] = useState<boolean>(false);
    const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | undefined>(undefined);
    const [tableColumns, setTableColumns] = useState<TableColumnDefinition<T>[]>([]);
    const [selectedRows, setSelectedRows] = useState(new Set<TableRowId>([]));

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const gridBodyClasses = mergeClasses(
        styles.gridBody,
        !!bodyBorder && styles.gridBodyBorder,
        commonStyles.scroll.classes.scrollbarClasses,
    );

    //Selection functionality
    const selectJustThisRow = (tableRowId: TableRowId) => {
        setSelectedRows(new Set<TableRowId>([tableRowId]));
        !!onSelectionChanged && onSelectionChanged(items.filter((item: any) => item[rowId as string] == tableRowId));
    };
    const selectFirst = () => {
        if (!!selectedRows.size) {
            selectJustThisRow([...selectedRows][0]);
        }
    };
    const onSelectionChange: DataGridProps['onSelectionChange'] = (_, data) => {
        setSelectedRows(data.selectedItems);
        !!onSelectionChanged &&
            onSelectionChanged(items.filter((item: any) => data.selectedItems.has(item[rowId as string] as TableRowId)));
    };

    //Columns and Logic
    const colSizeOptions: TableColumnSizingOptions = {};
    const getColumns = () => {
        const actualColumns = [] as TableColumnDefinition<T>[];

        columns?.forEach((col: CustomDataTableColumn<T>) => {
            colSizeOptions[col.id] = {
                defaultWidth: col.defaultWidth,
                idealWidth: col.defaultWidth,
                minWidth: col.minWidth,
            };

            const newColumn = createTableColumn<T>({
                columnId: col.id,
                compare: col.compare,
                renderHeaderCell: () => {
                    const headerClasses = mergeClasses(
                        styles.headerCell,
                        col.justifyContent === 'start' && styles.cellJustifyStart,
                        col.justifyContent === 'center' && styles.cellJustifyCenter,
                        col.justifyContent === 'end' && styles.cellJustifyEnd,
                    );
                    return (
                        <TableCellLayout className={headerClasses} style={{ textAlign: col.justifyContent }}>
                            {col.header}
                        </TableCellLayout>
                    );
                },
                renderCell: (item) => {
                    const cellClasses = mergeClasses(
                        styles.cell,
                        col.pointerEvents && styles.cellPointerOn,
                        col.userSelect && styles.cellUserSelectOn,
                        col.justifyContent === 'start' && styles.cellJustifyStart,
                        col.justifyContent === 'center' && styles.cellJustifyCenter,
                        col.justifyContent === 'end' && styles.cellJustifyEnd,
                        col.border && styles.cellBorder,
                    );

                    // if (!!col.primary && !!showActions) {
                    //     return (
                    //         <Row flex='1' noGap noWrap>
                    //             <Column flex='1' className={mergeClasses(cellClasses)}>
                    //                 {col.renderCell(item)}
                    //             </Column>
                    //             <Column width='auto' align='right' vAlign='middle'>
                    //                 <ToolbarButton
                    //                     className={commonStyles.button.root}
                    //                     icon={<MoreVerticalRegular />}
                    //                     aria-label='More items'
                    //                     appearance='subtle'
                    //                     onClick={(e: React.MouseEvent) => {
                    //                         clickMoreButton(e, (item as any)[rowId] as TableRowId);
                    //                     }}
                    //                 />
                    //             </Column>
                    //         </Row>
                    //     );
                    // } else {
                        return <TableCellLayout className={cellClasses}>{col.renderCell(item)}</TableCellLayout>;
                    //}
                },
            });

            if (col.show != false) {
                actualColumns.push(newColumn);
            }
        });

        // actualColumns.push(emptyCol());
        // colSizeOptions['@empty'] = {
        //     defaultWidth: 50,
        //     idealWidth: 50,
        //     minWidth: 50,
        // };

        if (!!showActions) {
            actualColumns.push(actionsCol());
            colSizeOptions['@actions'] = {
                defaultWidth: 150,
                idealWidth: 150,
                minWidth: 50,
            };
        }

        setTableColumns(actualColumns);
    };

    const singleSelectAction = (item: T, act: CustomDataTableAction<T>, e: React.MouseEvent<any>) => {
        if (act.singleSelectItemOnClick) {
            e.stopPropagation();
            selectJustThisRow(item[rowId as keyof T] as TableRowId);
        }
    };

    const renderAction = (act: CustomDataTableAction<T>, item: T) => {
        const btnClasses = mergeClasses(commonStyles.button.root, !act.noHideOnHover?.(item) && 'hidden-btn');
        const action = (
            <Tooltip relationship='label' visible={!!act.tooltip ? undefined : false} content={act?.tooltip || ''}>
                <ToolbarButton
                    className={btnClasses}
                    aria-label={act.ariaLabel}
                    appearance='subtle'
                    icon={typeof act?.icon === 'function' ? act?.icon(item) : act?.icon || null}
                    onClick={(e) => {
                        singleSelectAction(item, act, e);
                        !!act.onClick && act.onClick(item, e);
                    }}
                    disabled={typeof act.disabled === 'function' ? act.disabled(item) : act.disabled}
                />
            </Tooltip>
        );

        if (act.divider) return <ToolbarDivider className={mergeClasses(btnClasses, styles.actionDivider)} />;
        else return action;
    };

    const emptyCol = (): TableColumnDefinition<T> => {
        return createTableColumn<T>({
            columnId: '@empty',
            renderHeaderCell: () => {
                return <TableCellLayout className={styles.headerCell}></TableCellLayout>;
            },
        });
    };

    const actionsCol = (): TableColumnDefinition<T> => {
        return createTableColumn<T>({
            columnId: '@actions',
            renderHeaderCell: () => {
                return <TableCellLayout className={styles.headerCell}></TableCellLayout>;
            },
            renderCell: (item: T) => {
                const filteredActions = actions?.filter((act) => !act.canAccess || act.canAccess(item)) || [];

                return (
                    <Column className={styles.endCell} align='right' vAlign='middle'>
                        {!!filteredActions.length && (
                            <Toolbar className={styles.actionsToolbar}>
                                {filteredActions.map((act) => (
                                    <React.Fragment key={act.id}>
                                        {act?.renderDialog
                                            ? act?.renderDialog(item, renderAction(act, item))
                                            : renderAction(act, item)}
                                    </React.Fragment>
                                ))}
                                <ToolbarButton
                                    className={commonStyles.button.root}
                                    icon={<MoreVertical20Regular />}
                                    aria-label='More items'
                                    appearance='subtle'
                                    onClick={(e: React.MouseEvent) => {
                                        clickMoreButton(e, (item as any)[rowId] as TableRowId);
                                    }}
                                />
                            </Toolbar>
                        )}
                    </Column>
                );
            },
        });
    };

    const clickMoreButton = (e: any, rowId: TableRowId) => {
        e.stopPropagation();

        handleContextMenu(e, rowId);
    };

    const getCellFocusMode = (columnId: TableColumnId): DataGridCellFocusMode => {
        if (columnId == '@actions') return 'none';
        const col = columns?.find((x) => x.id == columnId);
        if (!!col) return col.cellFocusMode || 'cell';
        else return 'none';
    };

    const handleContextMenu = (event: React.MouseEvent, rowId: TableRowId) => {
        event.preventDefault();
        event.stopPropagation();

        selectJustThisRow(rowId);
        setMenuPosition({ x: event.clientX, y: event.clientY });
        setMenuOpen(true);
    };

    const closeTableContextMenu = () => {
        setMenuPosition(undefined);
        setMenuOpen(false);
    };

    const handleHeaderRowContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        // Future implementation for header context menu
    };

    //Effects
    useEffect(() => {
        if (singleSelect) {
            selectFirst();
        }
    }, [singleSelect]);

    useEffect(() => {
        getColumns();
    }, [columns]);

    useEffect(() => {
        setSelectedRows(new Set<TableRowId>([]));
    }, [clearSelectionToken]);

    return (
        <div className={styles.root}>
            <div className={styles.dataGridContainer}>
                <DataGrid
                    items={items}
                    columns={tableColumns}
                    resizableColumns={true}
                    columnSizingOptions={colSizeOptions}
                    sortable={sortable}
                    subtleSelection={select}
                    selectionMode={select ? (singleSelect ? 'single' : 'multiselect') : undefined}
                    selectionAppearance={select ? 'brand' : undefined}
                    selectedItems={select ? selectedRows : undefined}
                    onSelectionChange={select ? onSelectionChange : undefined}
                    getRowId={(x) => x[rowId]}
                >
                    <DataGridHeader onContextMenu={handleHeaderRowContextMenu}>
                        <DataGridRow
                            key='headerRow'
                            className={styles.headerRow}
                            selectionCell={
                                select
                                    ? {
                                          checkboxIndicator: { 'aria-label': 'Select all rows' },
                                      }
                                    : undefined
                            }
                        >
                            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
                        </DataGridRow>
                    </DataGridHeader>
                    <DataGridBody<T> className={gridBodyClasses} style={{ height: bodyHeight }}>
                        {({ item, rowId }) => (
                            <DataGridRow<T>
                                key={rowId}
                                className={styles.dataGridRow}
                                selectionCell={
                                    select
                                        ? {
                                              checkboxIndicator: { 'aria-label': 'Select case' },
                                          }
                                        : undefined
                                }
                                onContextMenu={(e: React.MouseEvent) => handleContextMenu(e, rowId)}
                                onDoubleClick={(e: React.MouseEvent) => onDoubleClickItem && onDoubleClickItem(item, e)}
                                onAuxClick={() => {
                                    !select && selectJustThisRow(rowId);
                                }}
                            >
                                {({ renderCell, columnId }) => (
                                    <DataGridCell focusMode={getCellFocusMode(columnId)}>{renderCell(item)}</DataGridCell>
                                )}
                            </DataGridRow>
                        )}
                    </DataGridBody>
                </DataGrid>
            </div>
            {!!showActions && !!actions?.length && (
                <CustomDataTableContextMenu<T>
                    open={menuOpen}
                    item={items.find((item: any) => !!selectedRows.size && item[rowId] == [...selectedRows][0])}
                    actions={actions}
                    menuPosition={menuPosition}
                    onOpenChange={(isOpen: boolean) => !isOpen && closeTableContextMenu()}
                />
            )}
        </div>
    );
};
